import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Button, Chip, Link, Stack, Typography } from "@mui/material";
import { Event as EventIcon } from "../icons";
import { CalendarMonth as CalendarMonthIcon } from "../icons";
import { LocationOn as LocationOnIcon } from "../icons";
import { Schedule as ScheduleIcon } from "../icons";
import { Link as LinkIcon } from "../icons";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { T } from "./Translate";
import PublicPageLayout, { useAuthUser } from "../components/PublicPageLayout";
import ListingImageSlideshow from "../components/ListingImageSlideshow";
import FollowItemButton, { FollowItemCountdown } from "../components/FollowItemButton";
import CopyToClipboardComponent from "./CopyToClipboardButton";
import { formatEventDatesList, getHorarioLabel, sellsTicketsWithPaypal } from "./eventsUtils";
import { brandColors } from "../theme";
import { safeHttpUrl } from "../utils/safeUrl";

const InfoRow = ({ icon, children }) => (
  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ color: "text.secondary" }}>
    {icon}
    <Typography sx={{ fontSize: 15 }}>{children}</Typography>
  </Stack>
);

// Ficha pública de un evento (/events/:eventId): se ve sin iniciar sesión y
// figura en el sitemap. Un evento privado solo lo ven su organizador y sus
// participantes (y no figura en el sitemap). Asistir/comprar entrada sigue
// pasando por /events.
const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { checked, user } = useAuthUser();
  const [rawEvent, setRawEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getDoc(doc(db, "events", eventId))
      .then((snap) => {
        if (!active) return;
        setRawEvent(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      })
      .catch((e) => console.error("Error al obtener el evento:", e))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [eventId]);

  const canSee =
    rawEvent &&
    (!rawEvent.isPrivate ||
      (user && (rawEvent.uid === user.uid || rawEvent.participantIds?.includes(user.uid))));
  const event = canSee ? rawEvent : null;
  const dates = event ? formatEventDatesList(event) : "";
  const horario = getHorarioLabel(event?.horario);

  // Pago sin PayPal: la entrada se compra en el enlace del organizador.
  const buyOnExternalLink = event?.isPaid && !sellsTicketsWithPaypal(event) && !!event?.url;

  const handleGoToEvent = () => {
    if (user) navigate(`/events?highlight=${eventId}`);
    else navigate("/login", { state: { from: location } });
  };

  return (
    <PublicPageLayout
      title={event?.title || <T>Evento</T>}
      loading={loading || !checked}
      notFound={!event && <T>No se encontró el evento.</T>}
      isGuest={!user}
      showAd
    >
      <Stack spacing={2.5}>
        <ListingImageSlideshow
          images={event?.images}
          alt={event?.title}
          height={{ xs: 240, sm: 360 }}
          autoPlayMs={5000}
          PlaceholderIcon={EventIcon}
          placeholderText={<T>No hay imágenes disponibles</T>}
        />

        <Box>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
            {event?.eventType && <Chip size="small" label={event.eventType} />}
            <Chip
              size="small"
              color={event?.isPaid ? "warning" : "success"}
              label={event?.isPaid ? `$ ${Number(event.ticketPrice || 0).toLocaleString()}` : <T>Gratis</T>}
            />
          </Stack>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
            {event?.title}
          </Typography>
          {event?.name && (
            <Typography sx={{ color: "text.secondary", mt: 0.5 }}>
              <T>Organiza</T> {event.name}
            </Typography>
          )}
        </Box>

        <Stack spacing={1}>
          {dates && <InfoRow icon={<CalendarMonthIcon fontSize="small" />}>{dates}</InfoRow>}
          {horario && <InfoRow icon={<ScheduleIcon fontSize="small" />}>{horario}</InfoRow>}
          {event?.direccion && <InfoRow icon={<LocationOnIcon fontSize="small" />}>{event.direccion}</InfoRow>}
          {event?.url && (
            <InfoRow icon={<LinkIcon fontSize="small" />}>
              <Link href={safeHttpUrl(event.url) || undefined} target="_blank" rel="noopener noreferrer nofollow" sx={{ overflowWrap: "anywhere" }}>
                {event.url}
              </Link>
            </InfoRow>
          )}
        </Stack>

        {event && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <FollowItemButton type="event" item={event} variant="button" />
            <FollowItemCountdown type="event" item={event} sx={{ alignSelf: "center" }} />
          </Stack>
        )}

        {event?.url && event?.cupon && <CopyToClipboardComponent textToCopy={event.cupon} />}

        {event?.description && (
          <Typography sx={{ whiteSpace: "pre-line", color: "text.primary" }}>{event.description}</Typography>
        )}

        <Button
          variant="contained"
          size="large"
          {...(buyOnExternalLink
            ? { component: "a", href: event.url, target: "_blank", rel: "noopener noreferrer nofollow" }
            : { onClick: handleGoToEvent })}
          sx={{ textTransform: "none", fontWeight: 600, bgcolor: brandColors.primary, "&:hover": { bgcolor: brandColors.primaryHover } }}
        >
          {sellsTicketsWithPaypal(event) || buyOnExternalLink ? <T>Comprar entrada</T> : <T>Quiero asistir</T>}
        </Button>
      </Stack>
    </PublicPageLayout>
  );
};

export default EventDetail;
